import React, {Fragment} from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { translate } from "react-i18next"
import Tree from "./Tree"
import LocationTreeShape from "../../../state/shapes/LocationTreeShape"
import SearchInput from "./SearchInput"

const SearchArea = styled.div`
  margin-left: 20px;
`

// Override styles for the checkboxes
const TreeArea = styled.div`
  ul {
    list-style: none;
  }

  & span.rc-tree-checkbox {
    background: none !important;
    margin: -2px 6px 0 -1px !important;
  }

  & span.rc-tree-checkbox:before {
    font-family: FontAwesome;
    font-style: normal;
    color: #333;
    content: "\f096";
    width: 16px;
    height: 16px;
    font-size: 16px;
    line-height: 16px;
  }

  & span.rc-tree-checkbox-checked:before {
    color: ${props => props.primaryColor};
    content: "\f00c";
  }

  & span.rc-tree-checkbox-indeterminate:before {
    color: #ccc;
    content: "\f14a";
  }

  & span.rc-tree-title::selection {
    background: transparent;
  }
`

const SelectAllButton = styled.button`
  color: ${props => props.theme.primaryColor};
  border: 0;
  background: none;
  font-weight: normal;
  margin: 10px 0;
  outline: 0 !important;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`

class TreeContainer extends React.Component {
  static propTypes = {
    treeData: PropTypes.arrayOf(PropTypes.shape(LocationTreeShape)).isRequired,
    flatData: PropTypes.array.isRequired,
    defaultCheckedKeys: PropTypes.arrayOf(PropTypes.number),
    theme: PropTypes.shape({
      primaryColor: PropTypes.string.isRequired,
      primaryColorHover: PropTypes.string.isRequired,
      navBackgroundColor: PropTypes.string.isRequired
    }).isRequired,
    onCheckChange: PropTypes.func.isRequired
  }

  state = { checkedKeys: [], searchText: "", isExpanded: false,  flatDataIds: [], selectAll: false  }

  componentWillMount = () => {
    let isExpanded = false, selectAllIds = [], selectAll = false

    // If there is only one root level location, expand it automatically
    if (this.props.treeData.length === 1 && this.props.treeData[0].children) {
      isExpanded = true
    }

    this.props.flatData.forEach(function(item){
      selectAllIds.push(item.value.toString())
    })

    if (this.props.defaultCheckedKeys.length > 0) {
      selectAll = true
    }

        // Set any previous checked keys
    this.setState({ checkedKeys: this.props.defaultCheckedKeys, isExpanded, selectAll, flatDataIds: selectAllIds})
  }
  checkSearchAllowsInclusion = (value, childrenCount) => {
          // Allow if there is no search text
    return (!this.isTextSearch() || 
          // Allow if there is a search text and the label matches.
           (this.isTextSearch() && this.isSearchMatch(value.label, this.state.searchText)) ||
          // Allow if there is search text and it doesn't match... but it has children underneath.
           (this.isTextSearch() && !this.isSearchMatch(value.label, this.state.searchText) && childrenCount > 0))
  }

  getChildValues = value => {
    const children = this.props.flatData.filter(d => d.parentValue == value)
    const childKeys = []
    children.forEach(c => {     
      const subChildren = this.getChildValues(c.value)
      if (subChildren) {
        subChildren.forEach(sc => childKeys.push(sc.toString()))
      }

      if (this.checkSearchAllowsInclusion(c, subChildren.length)) {
        childKeys.push(c.value.toString())
      }
    })
    return childKeys
  }


  removeKeys = values => {
    const keys = this.state.checkedKeys.filter(ck => !values.includes(ck))
    if (keys) return keys
    return []
  }

  getParentValue = value => {
    const parent = this.props.flatData.filter(d => d.value == value)
    if (parent && parent.length === 1) return parent[0].parentValue.toString()

    return null
  }

  siblingsAllChecked = (siblings, keys) => {
    let allChecked = true

    // Unchecked parentvalue if any children aren't checked
    for (let i = 0; i < siblings.length; i++) {
      const siblingValue = siblings[i]
      if (!keys.includes(siblingValue)) allChecked = false
    }

    return allChecked
  }

  getValuesToRemove = (value, childValues) => {
    const valuesToDelete = []
    valuesToDelete.push(value)
    childValues.forEach(v => valuesToDelete.push(v))

    return valuesToDelete
  }

  updateCheckedKeys = value => {
    let selectAll = false
    const addValues = this.getChildValues(value)
    const removeValues = this.getValuesToRemove(value, addValues)

    // Unchecked box checked
    const addingValues = !this.state.checkedKeys.includes(value)

    const parentValue = this.getParentValue(value)
    if (parentValue && parentValue > 0) {
      const siblings = this.getChildValues(parentValue)

      // Remove parent value if all children aren't checked
      const checkedValues = this.state.checkedKeys.filter(k => !removeValues.includes(k))

      if (addingValues) {
        // Add selected value to checked values, so check against its sibilings includes it
        checkedValues.push(value)
        // Issue here where children on this value would also need to be added
        this.getChildValues(value).forEach(v => checkedValues.push(v))
      }

      // If all aren't checked, uncheck the parent value
      if (!this.siblingsAllChecked(siblings, checkedValues)) removeValues.push(parentValue)
      // If all are checked, check parent value
      else addValues.push(parentValue)
    }

    // Create new array without value, or child values
    const keys = this.removeKeys(removeValues)
    if (addingValues) {
      keys.push(value)
      addValues.forEach(v => keys.push(v))
    }

    if (keys.length > 0) {
      selectAll = true
    }

    this.setState({ checkedKeys: keys, selectAll })
    this.props.onCheckChange(keys)
  }

  handleSearchChange = event => {
    const searchText = event.target.value

    if (!searchText || searchText === "") {
      this.setState({
        searchText,
        isExpanded: false
      })
    } else {
      this.setState({
        searchText
      })
    }
  }

  isTextSearch = () => {
    const searchMinLength = 0
    return this.state.searchText.length > searchMinLength
  }

  // Does label contain search phrase
  isSearchMatch = (label, searchText) => {
    return label.toLowerCase().includes(searchText.toLowerCase())
  }

  shouldRenderChildren = (hasChildren, value, searchText, flatData, label) => {
    if (!hasChildren) return false
    if (!this.isTextSearch()) return true
    if (this.isSearchMatch(label, searchText)) return true

    const children = flatData.filter(d => d.parentValue == value)

    const childrenMatchSearch = this.childrenMatchSearch(children, searchText, flatData)
    return childrenMatchSearch
  }

  // Check if any child labels match the search phrase
  childrenMatchSearch = (children, searchText, flatData) => {
    const childLabels = this.getChildLabels(flatData, children)
    return childLabels.some(f => this.isSearchMatch(f, searchText))
  }

  getChildLabels = (flatData, children) => {
    const labels = []
    if (!children) return labels

    children.forEach(c => {
      labels.push(c.label)

      if (c.hasChildren) {
        const subChildren = flatData.filter(d => d.parentValue == c.value)
        const subLabels = this.getChildLabels(flatData, subChildren)

        subLabels.forEach(sl => labels.push(sl))
      }
    })

    return labels
  }

  toggleSelectAll = () => {
    if (this.state.selectAll == false) {
      this.setState({ checkedKeys: this.state.flatDataIds })
      this.props.onCheckChange(this.state.flatDataIds)
    } else {
      this.setState({ checkedKeys: [] })
      this.props.onCheckChange([])
    }

    this.setState(prevState => ({ selectAll: !prevState.selectAll  }))
  }

  render() {
    // eslint-disable-next-line react/prop-types
    const { treeData, flatData, theme, t, showSelectAll, useLargerCheckbox } = this.props

    const renderTree = (data, checkedKeys, text, flatData) => {
      const nodes = []

      data.forEach(d => {
        const shouldRender = this.shouldRenderChildren(
          true,
          d.value,
          this.state.searchText,
          flatData,
          d.label
        )

        if (shouldRender) {
          nodes.push(
            <Tree
              key={`key_${d.value}`}
              treeValue={d.value}
              treeData={d}
              checkedKeys={checkedKeys}
              onUpdate={this.updateCheckedKeys}
              searchText={text}
              flatData={flatData}
              isExpanded={this.state.isExpanded}
              shouldRenderChildrenFn={this.shouldRenderChildren}
              isSearchMatchFn={this.isSearchMatch}
              childrenMatchSearchFn={this.childrenMatchSearch}
              isTextSearchFn={this.isTextSearch}
              getChildValuesFn={this.getChildValues}
              useLargerCheckbox={useLargerCheckbox}
            />
          )
        }
      })

      if (nodes.length > 0) return nodes

      return <span>{t("Common.EmptyState.Search")}</span>
    }

    return (
      <TreeArea>
        <SearchArea>
          <SearchInput theme={theme} handleSearchChange={this.handleSearchChange} />
        </SearchArea>

        
          <Fragment>
            <hr style={{ marginBottom: "0px"}}/>

            <div className="row">
                <div className="col-0" style={{ paddingRight: "28px"}}>&nbsp;</div>
                <div className="col-4">
                  {showSelectAll && this.state.searchText.length == 0 && (
                    <SelectAllButton onClick={this.toggleSelectAll} primaryColor={theme.primaryColor}>
                      {this.state.selectAll ? t("Common.UnselectAll") : t("Common.SelectAll")}
                    </SelectAllButton>
                  )}
                </div>
            </div>
          </Fragment>
        
        <ul>{renderTree(treeData, this.state.checkedKeys, this.state.searchText, flatData)}</ul>
      </TreeArea>
    )
  }
}

TreeContainer.propTypes = {
  showSelectAll: PropTypes.bool,
  useLargerCheckbox: PropTypes.bool
}

TreeContainer.defaultProps = {
  showSelectAll: false,
  useLargerCheckbox: false
}

export default translate()(TreeContainer)



// WEBPACK FOOTER //
// ./src/components/layout/checkbox-tree/TreeContainer.jsx